/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */
import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { pack as d3pack, hierarchy as d3hierarchy } from 'd3-hierarchy';
import { WsContext, WsContextType } from './WsContext';
import { StatusContext, StatusContextType } from './StatusContext';
import { getProbeRoots, getProbeChildren, execControl, clearAllProbes } from '../data';
import { LiveMap } from './LiveMap';
import ProbeTree from './ProbeTree';

// ─── Interfaces ────────────────────────────────────────────────────────────────

interface TopNItem {
    probe_id: number;
    probe_type: string;
    queue_name?: 'jobs' | 'tasks';
    op_name?: string;
    op_type?: string;
    title?: string;
    rootId?: number;
    [k: string]: any;
}

interface PmStats {
    execState: 'stopped' | 'run_one' | 'running';
    runningProbes: Array<{ probe_id: number; probe_type: string; queue_name?: 'jobs' | 'tasks'; rootId?: number; op_type?: string; title?: string }>;
    runningQueueCounts?: { jobs: number; tasks: number };
    queueLimits?: { jobs: number; tasks: number };
    lastCmd: string;
    heartBeat: number;
    topn: {
        pending: TopNItem[];
        completed: TopNItem[];
        counts: { pending: number; completed: number; pending_jobs?: number; pending_tasks?: number };
    };
    priorityProbeId: number | null;
    priorityProbeType: string | null;
}

interface ExecutingProbeInfo {
    rootId: number;
    probe_type: string;
    op_type?: string;
    title?: string;
}

// ─── QueueBand ────────────────────────────────────────────────────────────────

interface QueueBandProps {
    items: TopNItem[];
    label: string;
    totalCount: number;
    onSelect: (item: TopNItem) => void;
    runningIds: Set<number>;
    priorityId: number | null;
    animatedIds?: Set<number>;
    showLeftDetail?: boolean;
    showRightDetail?: boolean;
    /** 'right' = near items anchor to right edge (input queue); 'left' = near items anchor to left edge (output queue) */
    anchor?: 'left' | 'right';
    height?: number;
}

function QueueBand({
    items,
    label,
    totalCount,
    onSelect,
    runningIds,
    priorityId,
    animatedIds,
    showLeftDetail = true,
    showRightDetail = true,
    anchor,
    height,
}: QueueBandProps) {
    const theme: any = useTheme();
    const truncateChipText = (text: string, width: number, fontSize: number) => {
        const raw = String(text || '');
        if (!raw) return '';
        const usableWidth = Math.max(12, width - 12);
        const approxCharWidth = fontSize * 0.62;
        const maxChars = Math.max(1, Math.floor(usableWidth / approxCharWidth));
        if (raw.length <= maxChars) return raw;
        if (maxChars <= 1) return raw.slice(0, 1);
        if (maxChars <= 3) return raw.slice(0, maxChars);
        return `${raw.slice(0, maxChars - 1)}…`;
    };
    const BAND_H = Math.max(72, Number(height || 170));
    const compact = BAND_H < 120;
    const svgWidth = 1600;
    const centerY = BAND_H / 2;

    // Items returned by backend keep identity, remainder are anonymous to preserve queue scale.
    const knownItems = items || [];
    const anonCount = Math.max(0, totalCount - knownItems.length);

    type RenderItem = {
        item?: TopNItem;
        anonymous: boolean;
        index: number;
        x: number;
        y: number;
        w: number;
        h: number;
        zone: 'left' | 'middle' | 'right'; // Track which zone each item is in
    };

    // Split into pipeline zones: left 3 (input), middle (bulk queue/pile), right 3 (output)
    const LEFT_COUNT = 2;
    const RIGHT_COUNT = 1;
    let poolItems = knownItems;
    const leftItems = showLeftDetail ? poolItems.slice(0, LEFT_COUNT) : [];
    if (showLeftDetail) {
        poolItems = poolItems.slice(leftItems.length);
    }
    const rightTake = showRightDetail ? Math.min(RIGHT_COUNT, poolItems.length) : 0;
    const rightItems = showRightDetail ? poolItems.slice(poolItems.length - rightTake) : [];
    const middleItems = showRightDetail ? poolItems.slice(0, poolItems.length - rightTake) : poolItems;

    const FULL_SIZE_H = compact ? 34 : 46;
    const FULL_SIZE_W = compact ? 112 : 136;
    const CARD_SPACING = FULL_SIZE_W + (compact ? 6 : 8);
    const NEAR_MARGIN = 85;  // px between item-0 and the near edge of the container

    const renderItems: RenderItem[] = [];

    // MIDDLE: d3.pack for the bulk queue + anonymous
    const MAX_RENDER = 300;
    const totalMiddleItems = middleItems.length + anonCount;
    const totalRender = Math.min(totalMiddleItems, MAX_RENDER);
    const packSize = 320;
    const packCenter = packSize / 2;

    // Add deterministic jitter based on current composition so the pile visibly
    // reflows as items are added/removed instead of staying in a static pattern.
    const compositionSalt =
        totalCount * 31 +
        knownItems.reduce((acc, it, idx) => acc + (it?.probe_id ?? idx) * (idx + 7), 0);
    const seedJitter = (seed: number) => {
        const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
        return x - Math.floor(x); // [0,1)
    };

    const packChildren = Array.from({ length: totalRender }, (_, i) => {
        const sourceId = i < middleItems.length ? (middleItems[i]?.probe_id ?? i) : (100000 + i);
        const jitter = 0.82 + seedJitter(sourceId + compositionSalt) * 0.5; // [0.82, 1.32)
        const base = Math.max(2, 96 / (1 + i * 0.75));
        return {
            index: i,
            value: Math.max(2, base * jitter),
        };
    });

    const packRoot = d3hierarchy<any>({ children: packChildren }).sum((d: any) => d.value ?? 0);
    d3pack<any>().size([packSize, packSize]).padding(1.2)(packRoot);

    // Find max radius in pile to compute PILE_EDGE_DIST automatically
    const scale = Math.min(1, BAND_H / packSize);
    let maxLeftExtent = 0;
    let maxRightExtent = 0;
    const pileLeaves = totalRender > 0 ? packRoot.leaves() : [];
    pileLeaves.forEach((leaf: any) => {
        const scaledX = (leaf.x - packCenter) * scale;
        const scaledR = leaf.r * scale;
        maxLeftExtent = Math.max(maxLeftExtent, Math.abs(scaledX - scaledR));
        maxRightExtent = Math.max(maxRightExtent, scaledX + scaledR);
    });
    const noPile = totalRender === 0;
    const PILE_EDGE_DIST = noPile ? 0 : Math.max(maxLeftExtent, maxRightExtent) + (compact ? 10 : 15);

    // Anchor-aware positioning:
    //   anchor='right' (pending): item-0 near right edge, items fan LEFT, pile to their left
    //   anchor='left'  (completed): item-0 near left edge, items fan RIGHT, pile to their right
    //   default: center layout, items to left of pile
    let nearEdgeX: number;
    let nearItemsDir: 1 | -1; // +1 = subsequent items grow right, -1 = grow left
    let pileX: number;
    let farEdgeX: number;    // far-side items start here
    let farItemsDir: 1 | -1;

    if (anchor === 'right') {
        nearEdgeX = svgWidth - NEAR_MARGIN;
        nearItemsDir = -1;
        pileX = nearEdgeX - LEFT_COUNT * CARD_SPACING - PILE_EDGE_DIST + 60;
        farEdgeX = pileX - PILE_EDGE_DIST - (compact ? 54 : 76);
        farItemsDir = -1;
    } else if (anchor === 'left') {
        nearEdgeX = NEAR_MARGIN;
        nearItemsDir = +1;
        pileX = nearEdgeX + LEFT_COUNT * CARD_SPACING + PILE_EDGE_DIST - (compact ? 42 : 60);
        farEdgeX = pileX + PILE_EDGE_DIST;
        farItemsDir = +1;
    } else {
        pileX = svgWidth / 2;
        nearEdgeX = pileX - PILE_EDGE_DIST;
        nearItemsDir = -1;
        farEdgeX = pileX + PILE_EDGE_DIST;
        farItemsDir = +1;
    }

    const useCenteredCardsOnly = noPile && knownItems.length > 0 && knownItems.length <= LEFT_COUNT + RIGHT_COUNT;

    if (useCenteredCardsOnly) {
        // Small queue with no pile — anchor cards to near edge
        if (anchor === 'right') {
            knownItems.forEach((item, i) => {
                renderItems.push({ item, anonymous: false, index: i, x: svgWidth - NEAR_MARGIN - i * CARD_SPACING, y: centerY, w: FULL_SIZE_W, h: FULL_SIZE_H, zone: 'left' });
            });
        } else if (anchor === 'left') {
            knownItems.forEach((item, i) => {
                renderItems.push({ item, anonymous: false, index: i, x: NEAR_MARGIN + i * CARD_SPACING, y: centerY, w: FULL_SIZE_W, h: FULL_SIZE_H, zone: 'left' });
            });
        } else {
            const startX = svgWidth / 2 - ((knownItems.length - 1) * CARD_SPACING) / 2;
            knownItems.forEach((item, i) => {
                renderItems.push({
                    item, anonymous: false, index: i,
                    x: startX + CARD_SPACING * i,
                    y: centerY, w: FULL_SIZE_W, h: FULL_SIZE_H,
                    zone: i < Math.floor(knownItems.length / 2) ? 'left' : 'right',
                });
            });
        }
    } else {
        // Near items: item-0 at nearEdgeX, subsequent items fan in nearItemsDir
        leftItems.forEach((item, i) => {
            renderItems.push({
                item, anonymous: false, index: i,
                x: nearEdgeX + nearItemsDir * CARD_SPACING * i,
                y: centerY, w: FULL_SIZE_W, h: FULL_SIZE_H, zone: 'left',
            });
        });

        // Far items: older/back-of-queue items on the far side of the pile
        rightItems.forEach((item, i) => {
            renderItems.push({
                item, anonymous: false, index: knownItems.length - RIGHT_COUNT + i,
                x: farEdgeX + farItemsDir * CARD_SPACING * i,
                y: centerY, w: FULL_SIZE_W, h: FULL_SIZE_H, zone: 'right',
            });
        });
    }

    // Add middle packed items (pile) centered at pileX
    pileLeaves.forEach((leaf: any) => {
        const i = leaf.data.index;
        const isAnon = i >= middleItems.length;
        const item = isAnon ? undefined : middleItems[i];
        const r = leaf.r;
        const scaledR = r * scale;
        const x = (leaf.x - packCenter) * scale + pileX;
        const y = (leaf.y - packCenter) * scale + centerY;
        const w = Math.max(8, scaledR * 1.8);
        const h = Math.max(8, scaledR * 1.4);
        renderItems.push({ item, anonymous: isAnon, index: LEFT_COUNT + i, x, y, w, h, zone: 'middle' });
    });

    // Label position: always near the near edge so it stays visible
    const labelX = anchor === 'left' ? 6 : svgWidth - 6;
    const labelAnchor = anchor === 'left' ? 'start' : 'end';

    return (
        <div style={{
            height: BAND_H,
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            justifyContent: anchor === 'right' ? 'flex-end' : 'flex-start',
        }}>
            <svg
                width={svgWidth}
                height={BAND_H}
                style={{ overflow: 'hidden', display: 'block', flexShrink: 0 }}
            >
                {renderItems.slice().reverse().map(({ item, anonymous, x, y, w, h, index }) => {
                    const probeId = item?.probe_id ?? null;
                    const isRunning = probeId !== null && runningIds.has(probeId);
                    const isPriority = probeId !== null && probeId === priorityId;
                    const stroke = isRunning
                        ? (theme.colors.nodeRunning || '#dc3545')
                        : isPriority
                        ? 'violet'
                        : (theme.colors.ctext || '#aaa');
                    const fill = anonymous
                        ? (theme.colors.bg3 || theme.colors.bg2 || theme.colors.bg || '#111')
                        : (theme.colors.bg2 || theme.colors.bg || '#111');
                    const canLabelFull = !anonymous && w >= 40;
                    const canLabelId = !anonymous && w >= 22;
                    const showType = !anonymous && w >= 48;
                    const showTinyDot = anonymous && w <= 10;
                    const isAnimated = probeId !== null && animatedIds?.has(probeId);
                    const rx = Math.max(2, Math.min(10, h * 0.34));
                    const titleFontSize = compact ? (canLabelFull ? 9.5 : 8.5) : (canLabelFull ? 10.5 : 9.5);
                    const titleText = truncateChipText(
                        item?.title || item?.op_name || item?.probe_type || '',
                        w,
                        titleFontSize,
                    );
                    return (
                        <g
                            key={anonymous ? `anon-${index}` : `probe-${probeId}`}
                            transform={`translate(${x},${y})`}
                            style={{
                                cursor: anonymous ? 'default' : 'pointer',
                                transition: 'transform 280ms ease-out',
                                animation: isAnimated ? 'dashQueuePop 600ms ease-out' : undefined,
                            }}
                            onClick={() => {
                                if (!anonymous && item) onSelect(item);
                            }}
                        >
                            {showTinyDot ? (
                                <circle
                                    cx={0}
                                    cy={0}
                                    r={Math.max(2, w * 0.4)}
                                    fill="transparent"
                                    stroke={theme.colors.nodeGood || '#3ad26b'}
                                    strokeWidth={0.8}
                                />
                            ) : (
                            <rect
                                x={-w / 2}
                                y={-h / 2}
                                width={w}
                                height={h}
                                fill={fill}
                                stroke={stroke}
                                strokeWidth={isRunning ? 2.2 : 1}
                                rx={rx}
                            />
                            )}
                            {canLabelId && item && (
                                <text
                                    x={0}
                                    y={showType ? (compact ? -1 : -3) : (compact ? 3 : 4)}
                                    textAnchor="middle"
                                    fill={theme.colors.textMuted || theme.colors.ctext || '#888'}
                                    fontSize={compact ? (canLabelFull ? 11 : 10) : (canLabelFull ? 13 : 11)}
                                    fontFamily="monospace"
                                >
                                    {item.probe_id}
                                </text>
                            )}
                            {showType && item && (
                                <text
                                    x={0}
                                    y={compact ? 9 : 11}
                                    textAnchor="middle"
                                    fill={theme.colors.ctext || '#ccc'}
                                    fontSize={titleFontSize}
                                    fontFamily="monospace"
                                >
                                    {titleText}
                                </text>
                            )}
                        </g>
                    );
                })}
                {/* label */}
                <text
                    x={labelX} y={16}
                    textAnchor={labelAnchor}
                    fill={theme.colors.textMuted || '#888'}
                    fontSize={compact ? 10 : 11}
                    fontFamily="monospace"
                >
                    {label}  |  shown: {knownItems.length} + anon: {anonCount}
                </text>
            </svg>
        </div>
    );
}

// ─── ExecutingSlots ───────────────────────────────────────────────────────────

interface ExecutingSlotsProps {
    slots: Array<number | null>;
    executingProbes: Map<number, ExecutingProbeInfo>;
    onSelect: (probeId: number) => void;
    activeId: number | null;
    showRunningOverlay: boolean;
    vertical?: boolean;
}

function ExecutingSlots({ slots, executingProbes, onSelect, activeId, showRunningOverlay, vertical = false }: ExecutingSlotsProps) {
    const theme: any = useTheme();
    const slotCells: Array<number | null> = [...slots, null, null, null, null, null, null, null, null].slice(0, 8);
    return (
        <div style={{ position: 'relative', height: '100%' }}>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gridTemplateRows: 'repeat(4, minmax(0, 1fr))',
                gap: 4,
                padding: '4px',
                boxSizing: 'border-box',
                height: '100%',
            }}>
                {slotCells.map((probeId, i) => {
                    const info = probeId !== null ? executingProbes.get(probeId) : undefined;
                    const occupied = probeId !== null;
                    const isActive = probeId === activeId;
                    const borderColor = isActive
                        ? (theme.colors.nodeRunning || '#dc3545')
                        : occupied
                        ? (theme.colors.border || theme.colors.ctext || '#555')
                        : (theme.colors.textMuted || '#555');
                    return (
                        <div
                            key={i}
                            style={{
                                border: `2px ${occupied ? 'solid' : 'dashed'} ${borderColor}`,
                                borderRadius: 4,
                                backgroundColor: theme.colors.bg2 || theme.colors.bg || '#111',
                                cursor: occupied ? 'pointer' : 'default',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '0 6px',
                                overflow: 'hidden',
                                minWidth: 0,
                                minHeight: 0,
                            }}
                            onClick={() => { if (probeId !== null) onSelect(probeId); }}
                        >
                            {occupied && info ? (
                                <>
                                    <div style={{
                                        fontWeight: 700,
                                        fontSize: '0.78em',
                                        lineHeight: 1.05,
                                        color: theme.colors.nodeRunning || '#dc3545',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '100%',
                                    }}>
                                        #{probeId} {info.probe_type}
                                    </div>
                                    <div style={{
                                        fontSize: '0.68em',
                                        lineHeight: 1.05,
                                        color: theme.colors.textMuted || theme.colors.ctext || '#888',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '100%',
                                    }}>
                                        {info.title || `root #${info.rootId}`}
                                    </div>
                                </>
                            ) : (
                                <span style={{ color: theme.colors.textMuted || '#555', fontSize: '1.2em' }}>—</span>
                            )}
                        </div>
                    );
                })}
            </div>
            {showRunningOverlay && (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    zIndex: 2,
                }}>
                    <span style={{
                        fontFamily: 'monospace',
                        fontSize: '0.9em',
                        fontWeight: 700,
                        color: theme.colors.ctext || '#ccc',
                        animation: 'dashExecRainbow 2.4s linear infinite, dashExecPulse 1.1s ease-in-out infinite',
                        display: 'inline-block',
                        transformOrigin: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.45)',
                        border: `1px solid ${theme.colors.border || theme.colors.textMuted || '#666'}`,
                        borderRadius: 4,
                        padding: '2px 8px',
                    }}>
                        Running
                    </span>
                </div>
            )}
        </div>
    );
}

interface DashDetailPaneProps {
    theme: any;
    border: string;
    textMuted: string;
    promptText: string;
    responseText: string;
    logText: string;
    promptContainerRef: React.RefObject<HTMLDivElement | null>;
    responseContainerRef: React.RefObject<HTMLDivElement | null>;
    logContainerRef: React.RefObject<HTMLDivElement | null>;
}

function DashDetailPaneInner({
    theme,
    border,
    textMuted,
    promptText,
    responseText,
    logText,
    promptContainerRef,
    responseContainerRef,
    logContainerRef,
}: DashDetailPaneProps) {
    const panes = [
        { key: 'prompt', label: 'Prompt', value: promptText, ref: promptContainerRef },
        { key: 'response', label: 'Response', value: responseText, ref: responseContainerRef },
        { key: 'log', label: 'Log', value: logText, ref: logContainerRef },
    ];

    return (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div style={{
                flex: '0 0 44%',
                minHeight: 180,
                borderBottom: `1px solid ${border}`,
                backgroundColor: theme.colors.mapBg || theme.colors.bg2 || '#111',
                overflow: 'hidden',
            }}>
                <LiveMap
                    geoFill={theme.colors.bg3 || '#1c252f'}
                    geoStroke={theme.colors.textMuted || '#666'}
                    geoBg={theme.colors.mapBg || theme.colors.bg2 || '#111'}
                />
            </div>

            {panes.map((pane) => (
                <div key={pane.key} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', borderBottom: pane.key === 'log' ? 'none' : `1px solid ${border}`, backgroundColor: theme.colors.logBg || theme.colors.bg || '#000' }}>
                    <div style={{
                        height: 24,
                        flexShrink: 0,
                        padding: '4px 8px',
                        color: theme.colors.ctext || textMuted,
                        fontSize: '0.75em',
                        borderBottom: `1px solid ${border}`,
                        backgroundColor: theme.colors.logBg || theme.colors.bg || '#000',
                    }}>
                        {pane.label}
                    </div>
                    <div
                        ref={pane.ref}
                        style={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                            padding: 8,
                            backgroundColor: theme.colors.logBg || theme.colors.bg || '#000',
                            fontFamily: 'monospace',
                            fontSize: '0.75em',
                            whiteSpace: 'pre-wrap',
                            color: pane.key === 'prompt'
                                ? (theme.colors.promptTextColor || theme.colors.ctext || theme.colors.text || '#fff')
                                : pane.key === 'response'
                                ? (theme.colors.responseTextColor || theme.colors.ctext || theme.colors.text || '#fff')
                                : (theme.colors.ctext || theme.colors.text || '#fff'),
                        }}
                    >
                        {pane.value || <span style={{ color: textMuted }}>No {pane.label.toLowerCase()} yet.</span>}
                    </div>
                </div>
            ))}
        </div>
    );
}

const DashDetailPane = React.memo(DashDetailPaneInner);

const DashRightColumn = React.memo(function DashRightColumn() {
    const theme: any = useTheme();
    const wsContext = useContext(WsContext) as WsContextType;
    const border = theme.colors.border || theme.colors.ctext || '#444';
    const textMuted = theme.colors.textMuted || '#888';
    const [promptText, setPromptText] = useState('');
    const [responseText, setResponseText] = useState('');
    const [logData, setLogData] = useState<string[]>([]);
    const promptContainerRef = useRef<HTMLDivElement>(null);
    const responseContainerRef = useRef<HTMLDivElement>(null);
    const logContainerRef = useRef<HTMLDivElement>(null);
    const responseHasModelOutputRef = useRef(false);
    const responseLengthRef = useRef(0);

    useEffect(() => {
        if (!wsContext.ws) {
            return;
        }

        const onLog = (msg: any) => {
            const line = typeof msg === 'string' ? msg.trim() : JSON.stringify(msg);
            setLogData(prev => [...prev.slice(-119), line]);
        };

        const onReasoning = (msg: any) => {
            const line = typeof msg === 'string' ? msg.trim() : JSON.stringify(msg);
            if (!responseHasModelOutputRef.current) {
                setResponseText(prev => prev ? `${prev}\n[research] ${line}` : `[research] ${line}`);
            }
        };

        const onPrompt = (msg: any) => {
            setPromptText(typeof msg === 'string' ? msg : JSON.stringify(msg, null, 2));
            responseHasModelOutputRef.current = false;
            responseLengthRef.current = 0;
            setResponseText('');
            setLogData([]);
        };

        const onResponse = (msg: any) => {
            const nextText = typeof msg === 'string' ? msg : JSON.stringify(msg, null, 2);
            responseHasModelOutputRef.current = true;
            setResponseText(prev => prev === nextText ? prev : nextText);
        };

        const onResponseChunk = (msg: any) => {
            const chunk = String(msg || '');
            if (!chunk) {
                return;
            }
            if (!responseHasModelOutputRef.current) {
                responseHasModelOutputRef.current = true;
                setResponseText(prev => prev === chunk ? prev : chunk);
                return;
            }
            setResponseText(prev => prev + chunk);
        };

        wsContext.ws.on('log', onLog);
        wsContext.ws.on('reasoning', onReasoning);
        wsContext.ws.on('prompt', onPrompt);
        wsContext.ws.on('response', onResponse);
        wsContext.ws.on('responseChunk', onResponseChunk);

        return () => {
            wsContext.ws.off('log', onLog);
            wsContext.ws.off('reasoning', onReasoning);
            wsContext.ws.off('prompt', onPrompt);
            wsContext.ws.off('response', onResponse);
            wsContext.ws.off('responseChunk', onResponseChunk);
        };
    }, [wsContext]);

    useEffect(() => {
        const el = promptContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [promptText]);

    useEffect(() => {
        const el = responseContainerRef.current;
        if (!el) {
            return;
        }
        const prevLen = responseLengthRef.current;
        const nextLen = responseText.length;
        responseLengthRef.current = nextLen;
        if (nextLen <= prevLen) {
            return;
        }
        el.scrollTop = el.scrollHeight;
    }, [responseText]);

    useEffect(() => {
        const el = logContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [logData]);

    return (
        <DashDetailPane
            theme={theme}
            border={border}
            textMuted={textMuted}
            promptText={promptText}
            responseText={responseText}
            logText={logData.join('\n')}
            promptContainerRef={promptContainerRef}
            responseContainerRef={responseContainerRef}
            logContainerRef={logContainerRef}
        />
    );
});

// ─── DashExecStatus ───────────────────────────────────────────────────────────

function DashExecStatus() {
    const theme: any = useTheme();
    const navigate = useNavigate();
    const wsContext = useContext(WsContext) as WsContextType;
    const statusContext = useContext(StatusContext) as StatusContextType;

    const [pmStats, setPmStats] = useState<PmStats | null>(null);
    const [forests, setForests] = useState<Map<number, any[]>>(new Map());
    const [lastActiveId, setLastActiveId] = useState<number | null>(null);
    const [executingProbes, setExecutingProbes] = useState<Map<number, ExecutingProbeInfo>>(new Map());
    const [slotProbeIds, setSlotProbeIds] = useState<Array<number | null>>([]);
    const [selectedTask, setSelectedTask] = useState<{ rootId: number; probeId: number } | null>(null);
    const [autoRootId, setAutoRootId] = useState<number | null>(null);
    const [newPendingIds, setNewPendingIds] = useState<Set<number>>(new Set());
    const [pendingActions, setPendingActions] = useState<Set<'next' | 'all' | 'stop' | 'clear'>>(new Set());

    // Refs for stale-closure-safe access inside WS handlers
    const forestsRef = useRef<Map<number, any[]>>(new Map());
    const prevPendingIdsRef = useRef<Set<number>>(new Set());

    useEffect(() => { forestsRef.current = forests; }, [forests]);

    const addTree = useCallback(async (rootId: number) => {
        try {
            const children = await getProbeChildren(rootId);
            setForests(prev => new Map(prev).set(rootId, children));
        } catch (e: any) {
            console.error('DashExecStatus: error fetching probe children for root', rootId, e);
        }
    }, []);

    // On mount: load all probe roots; fetch children on demand
    useEffect(() => {
        getProbeRoots().then(async roots => {
            if (!roots.length) return;
            // Initialize with root node itself (no children yet) — fetch children on expand
            const entries = roots.map(r => [r.id, [
                {
                    id: r.id,
                    parent_id: null,
                    probe_type: r.probe_type,
                    passed: r.passed,
                    pending: r.pending,
                }
            ]] as [number, any[]]);
            setForests(new Map(entries));
        }).catch(e => console.error('DashExecStatus: error fetching probe roots', e));
    }, []);

    // WS subscriptions
    useEffect(() => {
        if (!wsContext.ws) return;

        const onProbeLifecycle = (msg: any) => {
            const { rootId, id, title, op_type, probe_type, running, passed, pending: isPending } = msg;
            if (!rootId || !id) return;

            if (running) {
                setLastActiveId(id);

                setExecutingProbes(prev => {
                    const next = new Map(prev);
                    const existing = next.get(id);
                    next.set(id, {
                        rootId,
                        probe_type: probe_type || existing?.probe_type || '?',
                        op_type: op_type || existing?.op_type,
                        title: title || existing?.title || String(id),
                    });
                    return next;
                });
            }

            if (!running && passed !== undefined) {
                setExecutingProbes(prev => {
                    const next = new Map(prev);
                    next.delete(id);
                    return next;
                });
                setSlotProbeIds(prev => prev.map((existingId) => existingId === id ? null : existingId));
            }

            const nodes = forestsRef.current.get(rootId);
            if (!nodes) {
                addTree(rootId);
                return;
            }
            const idx = nodes.findIndex((n: any) => n.id === id);
            if (idx === -1) {
                addTree(rootId);
                return;
            }
            const updated = [...nodes];
            updated[idx] = { ...updated[idx], running, passed, pending: isPending };
            setForests(prev => new Map(prev).set(rootId, updated));
        };

        const onPmStats = (msg: any) => {
            setPmStats(msg);
            const runningItems = (msg.runningProbes || [])
                .map((probe: any) => ({
                    probe_id: Number(probe.probe_id),
                    probe_type: probe.probe_type,
                    rootId: Number(probe.rootId || probe.probe_id),
                    op_type: probe.op_type,
                    title: probe.title,
                }))
                .filter((probe: any) => probe.probe_id > 0);
            const runningIds = new Set<number>(runningItems.map((probe: any) => probe.probe_id));
            setExecutingProbes(prev => {
                const next = new Map<number, ExecutingProbeInfo>();
                for (const probe of runningItems) {
                    const existing = prev.get(probe.probe_id);
                    next.set(probe.probe_id, {
                        rootId: probe.rootId || existing?.rootId || probe.probe_id,
                        probe_type: probe.probe_type || existing?.probe_type || '?',
                        op_type: probe.op_type || existing?.op_type,
                        title: probe.title || existing?.title || String(probe.probe_id),
                    });
                }
                return next;
            });
            setSlotProbeIds(prev => {
                const next: Array<number | null> = [...prev, null, null, null, null, null, null, null, null].slice(0, 8);
                const runningIds = new Set<number>(runningItems.map((probe: any) => probe.probe_id));

                for (let i = 0; i < next.length; i += 1) {
                    if (next[i] !== null && !runningIds.has(next[i] as number)) {
                        next[i] = null;
                    }
                }

                for (const probe of runningItems) {
                    if (next.includes(probe.probe_id)) {
                        continue;
                    }
                    const emptyIndex = next.findIndex((slotId) => slotId === null);
                    if (emptyIndex === -1) {
                        break;
                    }
                    next[emptyIndex] = probe.probe_id;
                }

                return next;
            });
            const currentPending = new Set<number>((msg.topn?.pending || []).map((p: any) => p.probe_id));
            const newlyPending = (msg.topn?.pending || [])
                .filter((p: any) => !prevPendingIdsRef.current.has(p.probe_id))
                .map((p: any) => p.probe_id);
            if (newlyPending.length > 0) {
                setNewPendingIds(new Set(newlyPending));
                setTimeout(() => {
                    setNewPendingIds(new Set());
                }, 600);
            }
            prevPendingIdsRef.current = currentPending;
        };

        wsContext.ws.on('probeLifecycle', onProbeLifecycle);
        wsContext.ws.on('pmStats', onPmStats);
        wsContext.ws.emit('refresh', {});

        return () => {
            wsContext.ws.off('probeLifecycle', onProbeLifecycle);
            wsContext.ws.off('pmStats', onPmStats);
        };
    }, [wsContext, addTree]);

    const runningRootIds = useMemo(() => {
        const seen = new Set<number>();
        const roots: number[] = [];
        slotProbeIds.forEach((probeId) => {
            if (probeId === null) {
                return;
            }
            const rootId = executingProbes.get(probeId)?.rootId;
            if (rootId && !seen.has(rootId)) {
                seen.add(rootId);
                roots.push(rootId);
            }
        });
        return roots;
    }, [slotProbeIds, executingProbes]);

    const pendingJobs = useMemo(() =>
        (pmStats?.topn.pending || []).filter((item: TopNItem) => item.queue_name === 'jobs')
    , [pmStats]);

    const pendingTasks = useMemo(() =>
        (pmStats?.topn.pending || []).filter((item: TopNItem) => item.queue_name !== 'jobs')
    , [pmStats]);

    const pendingHead = pendingTasks[0] || pendingJobs[0];
    const completedHead = pmStats?.topn.completed?.[0];
    const fallbackRootIds = useMemo(() => {
        if (runningRootIds.length > 0) return runningRootIds;
        if (pendingHead) return [pendingHead.rootId ?? pendingHead.probe_id];
        if (completedHead) return [completedHead.rootId ?? completedHead.probe_id];
        return [];
    }, [runningRootIds, pendingHead, completedHead]);

    useEffect(() => {
        if (selectedTask) return;
        if (fallbackRootIds.length === 0) {
            setAutoRootId(null);
            return;
        }
        setAutoRootId(prev => (prev !== null && fallbackRootIds.includes(prev)) ? prev : fallbackRootIds[0]);
    }, [fallbackRootIds, selectedTask]);

    const detailRootId = selectedTask?.rootId ?? autoRootId;
    const detailForest = useMemo(() =>
        detailRootId !== null
            ? new Map([[detailRootId, forests.get(detailRootId) || []]])
            : new Map<number, any[]>()
    , [detailRootId, forests]);

    useEffect(() => {
        if (detailRootId !== null && !forests.has(detailRootId)) {
            addTree(detailRootId);
        }
    }, [detailRootId, forests, addTree]);

    const runningIds = useMemo(() =>
        new Set<number>((pmStats?.runningProbes || []).map(p => p.probe_id))
    , [pmStats]);

    const hasAnyProbes = (pmStats?.topn.counts.pending ?? 0) > 0
        || (pmStats?.topn.counts.completed ?? 0) > 0
        || executingProbes.size > 0
        || forests.size > 0;

    const detailModeLabel = selectedTask
        ? `selected task #${selectedTask.probeId} in root #${selectedTask.rootId}`
        : runningRootIds.length > 0
        ? 'running roots'
        : pendingTasks.length > 0
        ? 'next queued task'
        : pendingJobs.length > 0
        ? 'next queued job'
        : completedHead
        ? 'most recently completed root'
        : 'no probes';

    const rootSummary = useCallback((rootId: number) => {
        const nodes = forests.get(rootId) || [];
        const rootNode = nodes.find((n: any) => Number(n.id) === rootId) || nodes[0];
        const runningInfo = Array.from(executingProbes.values()).find((info) => info.rootId === rootId);
        return {
            title: rootNode?.title || runningInfo?.title || `root #${rootId}`,
            subtitle: rootNode?.op_type || rootNode?.op_name || rootNode?.probe_type || runningInfo?.probe_type || '',
        };
    }, [forests, executingProbes]);

    const handleTaskSelect = useCallback((item: TopNItem) => {
        const rootId = item.rootId ?? item.probe_id;
        setSelectedTask({ rootId, probeId: item.probe_id });
        if (!forestsRef.current.has(rootId)) {
            addTree(rootId);
        }
    }, [addTree]);

    const handleExecutingSelect = useCallback((probeId: number) => {
        const info = executingProbes.get(probeId);
        if (!info) return;
        setSelectedTask({ rootId: info.rootId, probeId });
        if (!forestsRef.current.has(info.rootId)) {
            addTree(info.rootId);
        }
    }, [executingProbes, addTree]);

    const bandBg = theme.colors.bg2 || theme.colors.bg || '#111';
    const border = theme.colors.border || theme.colors.ctext || '#444';
    const textMuted = theme.colors.textMuted || '#888';
    const execState = pmStats?.execState ?? 'stopped';
    const runningProbeCount = pmStats?.runningProbes?.length ?? 0;
    const isExecutorBusy = runningProbeCount > 0 || execState === 'running' || execState === 'run_one';
    const execStateLabel = pmStats
        ? (isExecutorBusy ? 'running' : execState)
        : '—';
    const topBandHeight = 170;
    const stackedQueueBandHeight = 82;

    const runAction = useCallback(async (
        action: 'next' | 'all' | 'stop' | 'clear',
        fn: () => Promise<any>,
    ) => {
        if (pendingActions.has(action)) {
            return;
        }
        setPendingActions(prev => {
            const next = new Set(prev);
            next.add(action);
            return next;
        });
        try {
            await fn();
        } catch (err) {
            console.error(err);
        } finally {
            setPendingActions(prev => {
                const next = new Set(prev);
                next.delete(action);
                return next;
            });
        }
    }, [pendingActions]);

    return (
        <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.colors.bg || '#000',
            color: theme.colors.text || '#fff',
            overflow: 'hidden',
        }}>
            {/* PIPELINE ROW: left queue stack | executing slots | completed queue */}
            <div style={{
                height: topBandHeight,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'row',
                backgroundColor: bandBg,
                borderBottom: `1px solid ${border}`,
            }}>
                {/* Left queue stack */}
                <div style={{ flex: 1, overflow: 'hidden', minWidth: 0, paddingRight: 10, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', paddingBottom: 5 }}>
                        <QueueBand
                            items={pendingJobs}
                            label={`jobs: ${pmStats?.topn.counts.pending_jobs ?? 0}`}
                            totalCount={pmStats?.topn.counts.pending_jobs ?? 0}
                            onSelect={handleTaskSelect}
                            runningIds={runningIds}
                            priorityId={pmStats?.priorityProbeId ?? null}
                            animatedIds={newPendingIds}
                            anchor="right"
                            height={stackedQueueBandHeight}
                        />
                    </div>
                    <div style={{ height: 1, flexShrink: 0, backgroundColor: border, marginBottom: 5 }} />
                    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        <QueueBand
                            items={pendingTasks}
                            label={`tasks: ${pmStats?.topn.counts.pending_tasks ?? 0}`}
                            totalCount={pmStats?.topn.counts.pending_tasks ?? 0}
                            onSelect={handleTaskSelect}
                            runningIds={runningIds}
                            priorityId={null}
                            animatedIds={newPendingIds}
                            anchor="right"
                            height={stackedQueueBandHeight}
                        />
                    </div>
                </div>
                {/* Divider */}
                <div style={{ width: 1, flexShrink: 0, backgroundColor: border }} />
                {/* Executing slots — controls at top, vertical stack of slots below */}
                <div style={{ width: 360, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <style>{`
                        @keyframes dashExecSpin {
                            from { transform: rotate(0deg); }
                            to   { transform: rotate(360deg); }
                        }
                        @keyframes dashQueuePop {
                            from { transform: scale(0.82); opacity: 0.2; }
                            to   { transform: scale(1);    opacity: 1; }
                        }
                        @keyframes dashExecRainbow {
                            0%   { color: #ff5f6d; text-shadow: 0 0 6px rgba(255, 95,109,0.45); }
                            20%  { color: #ffc371; text-shadow: 0 0 6px rgba(255,195,113,0.45); }
                            40%  { color: #f9f871; text-shadow: 0 0 6px rgba(249,248,113,0.45); }
                            60%  { color: #7dffb3; text-shadow: 0 0 6px rgba(125,255,179,0.45); }
                            80%  { color: #70d6ff; text-shadow: 0 0 6px rgba(112,214,255,0.45); }
                            100% { color: #c77dff; text-shadow: 0 0 6px rgba(199,125,255,0.45); }
                        }
                        @keyframes dashExecPulse {
                            0%   { transform: scale(1); }
                            50%  { transform: scale(1.08); }
                            100% { transform: scale(1); }
                        }
                    `}</style>
                    <div style={{
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 6,
                        padding: '4px 6px',
                        borderBottom: `1px solid ${border}`,
                        fontSize: '0.8em',
                        backgroundColor: bandBg,
                    }}>
                        <span style={{ color: textMuted, fontFamily: 'monospace', marginRight: 8 }}>
                            j{pmStats?.runningQueueCounts?.jobs ?? 0}/{pmStats?.queueLimits?.jobs ?? 1}
                            &nbsp;t{pmStats?.runningQueueCounts?.tasks ?? 0}/{pmStats?.queueLimits?.tasks ?? 20}
                        </span>
                        {(['all', 'stop'] as const).map(cmd => (
                            (() => {
                                const isPending = pendingActions.has(cmd);
                                const isStart = cmd === 'all';
                                const baseColor = cmd === 'stop'
                                    ? (theme.colors.nodeRunning || '#dc3545')
                                    : cmd === 'all'
                                    ? (theme.colors.ctext || '#6699ff')
                                    : (theme.colors.textMuted || '#888');
                                const isActive = isStart
                                    ? isExecutorBusy
                                    : cmd === 'stop'
                                    ? !isExecutorBusy
                                    : false;
                                return (
                                    <button
                                        key={cmd}
                                        disabled={isPending}
                                        onClick={() => runAction(cmd, () => execControl(cmd))}
                                        style={{
                                            padding: '1px 6px',
                                            fontSize: '0.75em',
                                            lineHeight: '1.2',
                                            border: `1px solid ${baseColor}`,
                                            borderRadius: 3,
                                            backgroundColor: isPending
                                                ? (theme.colors.bg3 || '#222')
                                                : isActive
                                                ? 'rgba(64, 192, 255, 0.12)'
                                                : 'transparent',
                                            color: baseColor,
                                            cursor: isPending ? 'wait' : 'pointer',
                                            fontFamily: 'monospace',
                                            opacity: isPending ? 0.9 : 1,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 5,
                                        }}
                                    >
                                        {isPending && (
                                            <span
                                                style={{
                                                    width: 9,
                                                    height: 9,
                                                    borderRadius: '50%',
                                                    border: `1.5px solid ${baseColor}`,
                                                    borderTopColor: 'transparent',
                                                    animation: 'dashExecSpin 650ms linear infinite',
                                                    display: 'inline-block',
                                                }}
                                            />
                                        )}
                                        {cmd === 'all' ? 'start' : cmd}
                                    </button>
                                );
                            })()
                        ))}
                        <button
                            disabled={pendingActions.has('clear')}
                            onClick={() => runAction('clear', () => clearAllProbes())}
                            title="Clear all probes"
                            style={{
                                padding: '1px 6px',
                                fontSize: '0.75em',
                                lineHeight: '1.2',
                                border: `1px solid ${theme.colors.textMuted || '#888'}`,
                                borderRadius: 3,
                                backgroundColor: pendingActions.has('clear') ? (theme.colors.bg3 || '#222') : 'transparent',
                                color: theme.colors.textMuted || '#888',
                                cursor: pendingActions.has('clear') ? 'wait' : 'pointer',
                                fontFamily: 'monospace',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                            }}
                        >
                            {pendingActions.has('clear') && (
                                <span
                                    style={{
                                        width: 9,
                                        height: 9,
                                        borderRadius: '50%',
                                        border: `1.5px solid ${theme.colors.textMuted || '#888'}`,
                                        borderTopColor: 'transparent',
                                        animation: 'dashExecSpin 650ms linear infinite',
                                        display: 'inline-block',
                                    }}
                                />
                            )}
                            clear
                        </button>
                        <button
                            onClick={() => navigate('/ai/op-selector')}
                            style={{
                                padding: '1px 6px',
                                fontSize: '0.75em',
                                lineHeight: '1.2',
                                border: `1px solid ${theme.colors.info || theme.colors.ctext || '#66d9ef'}`,
                                borderRadius: 3,
                                backgroundColor: 'transparent',
                                color: theme.colors.info || theme.colors.ctext || '#66d9ef',
                                cursor: 'pointer',
                                fontFamily: 'monospace',
                            }}
                        >
                            AiOp
                        </button>
                        <span style={{
                            color: theme.colors.ctext || '#ccc',
                            fontFamily: 'monospace',
                            animation: isExecutorBusy
                                ? 'dashExecRainbow 2.4s linear infinite, dashExecPulse 1.1s ease-in-out infinite'
                                : undefined,
                            display: 'inline-block',
                            transformOrigin: 'center',
                        }}>
                            {execStateLabel}
                        </span>
                    </div>
                    <div style={{ flex: 1, minHeight: 0 }}>
                        <ExecutingSlots
                            slots={slotProbeIds}
                            executingProbes={executingProbes}
                            onSelect={handleExecutingSelect}
                            activeId={selectedTask?.probeId ?? lastActiveId}
                            showRunningOverlay={isExecutorBusy}
                            vertical={true}
                        />
                    </div>
                </div>
                {/* Divider */}
                <div style={{ width: 1, flexShrink: 0, backgroundColor: border }} />
                {/* Output queue */}
                <div style={{ flex: 1, overflow: 'hidden', minWidth: 0, paddingLeft: 10 }}>
                    <QueueBand
                        items={pmStats?.topn.completed || []}
                        label={`completed: ${pmStats?.topn.counts.completed ?? 0}`}
                        totalCount={pmStats?.topn.counts.completed ?? 0}
                        onSelect={handleTaskSelect}
                        runningIds={new Set<number>()}
                        priorityId={null}
                        showRightDetail={false}
                        anchor="left"
                    />
                </div>
            </div>

            {/* DETAIL PANELS */}
            <div style={{ flex: 1, display: 'flex', gap: 0, overflow: 'hidden', minHeight: 0 }}>
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <div style={{
                        height: 28,
                        flexShrink: 0,
                        fontSize: '0.75em',
                        color: textMuted,
                        padding: '5px 8px',
                        borderBottom: `1px solid ${border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                    }}>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            Tree view — {detailModeLabel}
                        </span>
                        {selectedTask && (
                            <button
                                onClick={() => setSelectedTask(null)}
                                style={{
                                    padding: '1px 6px',
                                    fontSize: '0.95em',
                                    lineHeight: '1.2',
                                    border: `1px solid ${textMuted}`,
                                    borderRadius: 3,
                                    backgroundColor: 'transparent',
                                    color: textMuted,
                                    cursor: 'pointer',
                                    fontFamily: 'monospace',
                                }}
                            >
                                dismiss
                            </button>
                        )}
                    </div>
                    {!selectedTask && runningRootIds.length > 1 && (
                        <div style={{
                            display: 'flex',
                            gap: 8,
                            padding: 8,
                            borderBottom: `1px solid ${border}`,
                            overflowX: 'auto',
                            flexShrink: 0,
                        }}>
                            {runningRootIds.map((rootId) => {
                                const summary = rootSummary(rootId);
                                const active = autoRootId === rootId;
                                return (
                                    <button
                                        key={rootId}
                                        onClick={() => setAutoRootId(rootId)}
                                        style={{
                                            minWidth: 140,
                                            textAlign: 'left',
                                            border: `1px solid ${active ? (theme.colors.info || theme.colors.ctext || '#66d9ef') : border}`,
                                            borderRadius: 6,
                                            backgroundColor: active ? (theme.colors.bg3 || '#222') : (theme.colors.bg2 || '#1b1f24'),
                                            color: theme.colors.text || '#fff',
                                            padding: '7px 9px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <div style={{ fontFamily: 'monospace', fontSize: '0.8em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            #{rootId}
                                        </div>
                                        <div style={{ fontSize: '0.7em', color: textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {summary.title}
                                        </div>
                                        {summary.subtitle && (
                                            <div style={{ fontSize: '0.66em', color: textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {summary.subtitle}
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        {!hasAnyProbes ? (
                            <div style={{ padding: 16, color: textMuted, fontSize: '0.9em' }}>
                                No probes in the system.
                            </div>
                        ) : detailRootId === null ? (
                            <div style={{ padding: 16, color: textMuted, fontSize: '0.9em' }}>
                                Waiting for a running, queued, or completed root to display.
                            </div>
                        ) : (
                            <ProbeTree
                                key={`${detailRootId ?? 'none'}:${selectedTask ? 'selected' : 'auto'}`}
                                forests={detailForest}
                                lastActiveId={selectedTask ? null : lastActiveId}
                                selectedNodeId={selectedTask?.probeId ?? null}
                                focusNodeId={selectedTask?.probeId ?? null}
                                initialCollapsed={new Set<number>()}
                                initialStick={!selectedTask}
                                onExpandNode={addTree}
                            />
                        )}
                    </div>
                </div>

                <div style={{ width: 1, flexShrink: 0, backgroundColor: border }} />

                <DashRightColumn />
            </div>

        </div>
    );
}

export default DashExecStatus;
